import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
    MdArrowBack,
    MdOutlineAdsClick,
    MdOutlinePersonAddAlt,
    MdOutlineTrendingUp,
    MdOutlineVerified,
} from 'react-icons/md';
import { FormInput, useToast } from '@podus/ui';
import StatCard from '@/components/StatCard';
import StatusBadge from '@/components/StatusBadge';
import RangeChips from '@/components/RangeChips';
import MetricsChart from '@/components/MetricsChart';
import ConfirmModal from '@/components/ConfirmModal';
import { api, ApiError } from '@/lib/api';
import { formatDate, formatDateTime, formatNumber, formatPercent } from '@/lib/format';
import { referralLinkFor } from '@/lib/referral';
import type { AmbassadorDetailPayload, RangePreset } from '@/types';

type PendingAction = 'suspend' | 'reactivate' | 'delete' | null;

/** Per-ambassador view mirroring their own dashboard, plus admin controls (FR-34/36). */
export default function AmbassadorDetail() {
    const { id } = useParams<{ id: string }>();
    const toast = useToast();
    const navigate = useNavigate();

    const [preset, setPreset] = useState<RangePreset>('30d');
    const [payload, setPayload] = useState<AmbassadorDetailPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [pending, setPending] = useState<PendingAction>(null);
    const [busy, setBusy] = useState(false);

    const [newCode, setNewCode] = useState('');
    const [savingCode, setSavingCode] = useState(false);

    const load = useCallback(
        async (range: RangePreset) => {
            if (!id) return;
            setLoading(true);
            try {
                const res = await api.get<{ data: AmbassadorDetailPayload }>(
                    `/ambassador/admin/ambassadors/${id}?preset=${range}`,
                );
                setPayload(res.response.data);
            } catch (err) {
                toast.error(err instanceof ApiError ? err.message : 'Could not load that ambassador');
            } finally {
                setLoading(false);
            }
        },
        [id, toast],
    );

    useEffect(() => {
        void load(preset);
    }, [preset, load]);

    const changeStatus = async (status: 'ACTIVE' | 'SUSPENDED' | 'DELETED', reason: string) => {
        if (!id) return;
        setBusy(true);
        try {
            await api.patch(`/ambassador/admin/ambassadors/${id}/status`, { status, reason });
            toast.success(`Ambassador ${status.toLowerCase()}`);
            setPending(null);
            if (status === 'DELETED') {
                navigate('/admin/ambassadors', { replace: true });
                return;
            }
            await load(preset);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not update that ambassador');
        } finally {
            setBusy(false);
        }
    };

    const overrideCode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id || !newCode.trim()) return;

        setSavingCode(true);
        try {
            await api.patch(`/ambassador/admin/ambassadors/${id}/code`, { code: newCode.trim().toLowerCase() });
            toast.success('Referral code updated — the previous code still resolves');
            setNewCode('');
            await load(preset);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not update the referral code');
        } finally {
            setSavingCode(false);
        }
    };

    if (loading && !payload) {
        return (
            <div className="mx-auto max-w-6xl px-4 py-8 lg:px-8">
                <div className="skeleton-shimmer h-64 w-full rounded-xl" />
            </div>
        );
    }

    if (!payload) {
        return (
            <div className="mx-auto max-w-6xl px-4 py-8 lg:px-8">
                <p className="text-[14px] text-[#747474]">That ambassador could not be found.</p>
                <Link to="/admin/ambassadors" className="mt-3 inline-block text-[14px] font-semibold text-primary">
                    Back to ambassadors
                </Link>
            </div>
        );
    }

    const { profile, metrics, series } = payload;

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-8">
            <Link
                to="/admin/ambassadors"
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#747474] transition-colors hover:text-secondary"
            >
                <MdArrowBack size={16} />
                All ambassadors
            </Link>

            <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-[22px] font-bold text-secondary">{profile.name ?? profile.email}</h2>
                        <StatusBadge status={profile.status === 'INVITED' ? profile.inviteStatus : profile.status} />
                    </div>
                    <p className="mt-1 text-[13px] text-[#747474]">{profile.email}</p>
                    {profile.suspendedReason && (
                        <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700">
                            Suspended: {profile.suspendedReason}
                        </p>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {profile.status === 'SUSPENDED' ? (
                        <button
                            type="button"
                            onClick={() => setPending('reactivate')}
                            className="rounded-lg bg-secondary px-4 py-2.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
                        >
                            Reactivate
                        </button>
                    ) : (
                        profile.status === 'ACTIVE' && (
                            <button
                                type="button"
                                onClick={() => setPending('suspend')}
                                className="rounded-lg border border-red-200 bg-white px-4 py-2.5 text-[13px] font-semibold text-red-600 transition-colors hover:bg-red-50"
                            >
                                Suspend
                            </button>
                        )
                    )}
                    <button
                        type="button"
                        onClick={() => setPending('delete')}
                        className="rounded-lg border border-[#E0E0E0] bg-white px-4 py-2.5 text-[13px] font-semibold text-[#747474] transition-colors hover:bg-[#f5f5f5]"
                    >
                        Delete
                    </button>
                </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <div className="rounded-xl border border-[#E0E0E0] bg-white px-5 py-3">
                    <p className="text-[12px] text-[#9a9a9a]">Referral link</p>
                    <p className="mt-0.5 font-mono text-[13px] text-secondary">
                        {payload.code ? referralLinkFor(payload.code) : '—'}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <Link
                        to={`/admin/daily?ambassadorId=${profile.id}`}
                        className="rounded-lg border border-[#E0E0E0] bg-white px-4 py-2.5 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5]"
                    >
                        Day-by-day
                    </Link>
                    <RangeChips value={preset} onChange={setPreset} disabled={loading} />
                </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard label="Link clicks" value={formatNumber(metrics.clicks)} Icon={MdOutlineAdsClick} />
                <StatCard label="Signups" value={formatNumber(metrics.signups)} Icon={MdOutlinePersonAddAlt} accent />
                <StatCard
                    label="Conversion rate"
                    value={formatPercent(metrics.conversionRate)}
                    Icon={MdOutlineTrendingUp}
                />
                <StatCard
                    label="Activated users"
                    value={formatNumber(metrics.activatedUsers)}
                    Icon={MdOutlineVerified}
                />
            </div>

            <div className="mt-6">
                <MetricsChart data={series} loading={loading} />
            </div>

            <div className="mt-6 grid gap-5 lg:grid-cols-2">
                {/* Account facts */}
                <div className="rounded-xl border border-[#E0E0E0] bg-white p-5">
                    <h3 className="text-[15px] font-bold text-secondary">Account</h3>
                    <dl className="mt-4 flex flex-col gap-3 text-[13px]">
                        {[
                            ['Invited', formatDate(profile.invitedAt)],
                            ['Accepted', formatDate(profile.acceptedAt)],
                            ['Last sign-in', formatDateTime(profile.lastLoginAt)],
                            ['Phone', profile.phoneNumber ?? '—'],
                            ['Code changes used', String(profile.codeChangesCount)],
                            ['Pending referrals', formatNumber(metrics.pendingReferrals)],
                            ['Voided referrals', formatNumber(metrics.voidedReferrals)],
                        ].map(([label, value]) => (
                            <div key={label} className="flex items-center justify-between gap-4">
                                <dt className="text-[#9a9a9a]">{label}</dt>
                                <dd className="text-right font-medium text-secondary">{value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Code override */}
                <form onSubmit={overrideCode} className="rounded-xl border border-[#E0E0E0] bg-white p-5">
                    <h3 className="text-[15px] font-bold text-secondary">Referral code</h3>
                    <p className="mt-1 text-[13px] leading-5 text-[#747474]">
                        Overriding bypasses the ambassador&rsquo;s own change limits. The old code is retired as a
                        permanent alias, never freed for reuse.
                    </p>

                    <p className="mt-4 text-[12px] font-semibold uppercase tracking-wide text-[#9a9a9a]">Current</p>
                    <p className="mt-1 font-mono text-[14px] text-secondary">{payload.code ?? '—'}</p>

                    {payload.aliases.length > 0 && (
                        <>
                            <p className="mt-4 text-[12px] font-semibold uppercase tracking-wide text-[#9a9a9a]">
                                Retired aliases (still resolve)
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {payload.aliases.map((alias) => (
                                    <span
                                        key={alias.code}
                                        className="rounded-full bg-[#f5f5f5] px-3 py-1 font-mono text-[12px] text-[#6b6b6b]"
                                    >
                                        {alias.code}
                                    </span>
                                ))}
                            </div>
                        </>
                    )}

                    <div className="mt-5">
                        <FormInput
                            id="admin-new-code"
                            title="New referral code"
                            placeholder="newcode"
                            variant="white"
                            value={newCode}
                            onChange={(e) => setNewCode(e.target.value.toLowerCase())}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={savingCode || !newCode.trim()}
                        className="mt-4 rounded-lg bg-secondary px-5 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {savingCode ? 'Updating…' : 'Override code'}
                    </button>
                </form>
            </div>

            <ConfirmModal
                open={pending === 'suspend'}
                title="Suspend this ambassador?"
                description="Their referral link stops attributing new signups immediately and they are signed out. Referrals they already earned are preserved."
                confirmLabel="Suspend"
                destructive
                requireReason
                reasonLabel="Reason for suspension"
                busy={busy}
                onConfirm={(reason) => changeStatus('SUSPENDED', reason)}
                onClose={() => setPending(null)}
            />

            <ConfirmModal
                open={pending === 'reactivate'}
                title="Reactivate this ambassador?"
                description="Their link starts attributing new signups again."
                confirmLabel="Reactivate"
                requireReason
                reasonLabel="Reason"
                busy={busy}
                onConfirm={(reason) => changeStatus('ACTIVE', reason)}
                onClose={() => setPending(null)}
            />

            <ConfirmModal
                open={pending === 'delete'}
                title="Delete this ambassador?"
                description="This is a soft delete — their referral history stays intact for reporting, but the account and link are disabled."
                confirmLabel="Delete account"
                destructive
                requireReason
                reasonLabel="Reason for deletion"
                busy={busy}
                onConfirm={(reason) => changeStatus('DELETED', reason)}
                onClose={() => setPending(null)}
            />
        </div>
    );
}
